/**
 * AirPlay while hls.js plays through ManagedMediaSource (SUP-3135).
 *
 * A ManagedMediaSource cannot be sent to an AirPlay receiver, so
 * hls-video-element adds the HLS playlist as a second <source>. When an AirPlay
 * route is picked, WebKit closes the ManagedMediaSource and hands the receiver
 * that <source> instead.
 *
 * hls.js 1.7 reads any `sourceclose` while media is attached as the WebKit
 * bfcache bug and resets the MediaSource (MEDIA_SOURCE_REQUIRES_RESET). The
 * reset re-attaches the media, which deletes the HLS <source>, turns remote
 * playback off and drops the route, so the receiver never plays.
 *
 * Measured on Safari 27: the close comes with the route already wireless, and
 * in the same instant the element's currentSrc moves to the HLS playlist while
 * `webkitCurrentPlaybackTargetIsWireless` briefly reads false. hls-video-element
 * restarts hls.js on that false, hls.js hits the closed MediaSource and asks
 * for another reset. So the wireless flag alone is not enough: once currentSrc
 * has left hls.js's blob, WebKit owns playback and hls.js must keep out.
 */

export const MEDIA_SOURCE_REQUIRES_RESET = 'mediaSourceRequiresReset';

type WirelessMedia = HTMLMediaElement & {
  webkitCurrentPlaybackTargetIsWireless?: boolean;
};

/** Whether WebKit has moved the element off hls.js's MediaSource onto the HLS <source>. */
export function isOnHlsAlternative(
  media: WirelessMedia | null | undefined,
): boolean {
  const currentSrc = media?.currentSrc;
  return !!currentSrc && !currentSrc.startsWith('blob:');
}

/** Whether this hls.js error is WebKit handing the media to an AirPlay route. */
export function isAirPlayHandover(
  details: string | undefined,
  media: WirelessMedia | null | undefined,
): boolean {
  if (isOnHlsAlternative(media)) return true;
  return details === MEDIA_SOURCE_REQUIRES_RESET &&
    media?.webkitCurrentPlaybackTargetIsWireless === true;
}

type ErrorData = { details?: string };
// deno-lint-ignore no-explicit-any
type ErrorControllerClass = new (...args: any[]) => {
  onErrorOut(event: string, data: ErrorData): void;
};

/**
 * hls.js's error controller with the AirPlay handover left alone. hls.js
 * registers `onErrorOut` (which performs the reset) before any listener the
 * player can add, so the `errorController` config option is the only place to
 * stop it.
 */
export function createAirPlayAwareErrorController<
  T extends ErrorControllerClass,
>(
  Base: T,
  getMedia: () => WirelessMedia | null | undefined,
): T {
  return class extends Base {
    override onErrorOut(event: string, data: ErrorData): void {
      if (isAirPlayHandover(data?.details, getMedia())) return;
      super.onErrorOut(event, data);
    }
  };
}

// deno-lint-ignore no-explicit-any
type GapControllerClass = new (...args: any[]) => { tick(): void };

/**
 * hls.js's gap controller kept off the playhead once WebKit plays the HLS
 * <source> itself. It runs on its own timer whether hls.js is loading or not,
 * and on a stall it nudges `currentTime`, which would seek the AirPlay session.
 */
export function createAirPlayAwareGapController<T extends GapControllerClass>(
  Base: T,
  getMedia: () => WirelessMedia | null | undefined,
): T {
  return class extends Base {
    override tick(): void {
      if (isOnHlsAlternative(getMedia())) return;
      super.tick();
    }
  };
}
